import {
  Component,
  computed,
  DestroyRef,
  effect,
  EventEmitter,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Output,
  signal,
  Signal,
  WritableSignal
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { PopupService } from '@app/core/services/popup.service';
import { PermissionPipe } from '@app/features/admin/settings/permission/pipes/permission.pipe';
import { VesselReportApi } from '@chartering/vessel-reports/vessel-reports.constants';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { BodyComponent } from '@shared/components/body/body.component';
import { BoxComponent } from '@shared/components/box/box.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { AutofillComponent } from '@shared/components/form/autofill/autofill.component';
import { TextboxComponent } from '@shared/components/form/textbox/textbox.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { InventorySuppliesComponent } from '@vessel-reports/rob/components/inventory-supplies/inventory-supplies.component';
import { RobActions } from '@vessel-reports/rob/state/rob.action';
import { RobService } from '@vessel-reports/rob/state/rob.service';
import { RobState } from '@vessel-reports/rob/state/rob.state';
import { Detail, Inventory, InventoryItem, RobReportDetail } from '@vessel-reports/rob/state/rob.types';
import { map, Subject, take, takeUntil, tap } from 'rxjs';

@Component({
  selector: 'edit-inventory-supply',
  imports: [
    BoxComponent,
    HeaderComponent,
    BodyComponent,
    FooterComponent,
    TextboxComponent,
    MatIconButton,
    MatIcon,
    AutofillComponent,
    TranslocoPipe,
    InventorySuppliesComponent,
    PermissionPipe
  ],
  templateUrl: './edit-inventory-supply.component.html',
  styleUrl: './edit-inventory-supply.component.scss'
})
export class EditInventorySupplyComponent implements OnInit, OnDestroy {
  protected readonly translocoService: TranslocoService = inject(TranslocoService);
  protected readonly popupService: PopupService = inject(PopupService);

  viewMode: WritableSignal<'add-edit' | 'select-inventory'> = signal('add-edit');
  detailReport: Signal<RobReportDetail> = computed(() => this.state.detailReport());
  selectedDetail: Signal<Detail> = computed(() => this.state.selectedDetail());
  inventories: Signal<Inventory[]> = computed(() => this.state.inventories());
  protected readonly VesselReportApi: typeof VesselReportApi = VesselReportApi;

  private readonly _robService: RobService = inject(RobService);
  protected readonly state: RobState = inject(RobState);
  private readonly _formBuilder: FormBuilder = inject(FormBuilder);
  private readonly _action: RobActions = inject(RobActions);
  private readonly _injector: Injector = inject(Injector);
  private readonly _matDialog: MatDialog = inject(MatDialog);
  private readonly _unsubscribeAll: Subject<void> = new Subject<void>();
  protected readonly destroyRef: DestroyRef = inject(DestroyRef);
  @Output() save: EventEmitter<void> = new EventEmitter<void>(); // declare output
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() close: EventEmitter<any> = new EventEmitter<any>();
  @Output() back: EventEmitter<any> = new EventEmitter<any>();
  form: FormGroup;

  ngOnInit() {
    this.form = this._formBuilder.group({
      seq: [''],
      id: [''],
      service_id: [''],
      vessel_report_id: [this.detailReport().id],
      report_item_id: [9],
      inventory_item_id: ['', [Validators.required]],
      description: [''],
      value: ['', [Validators.required]],
      remarks: [''],
      deleted_at: [''],
      inventory_item_name: [''],
      inventory_item_unit: ['L']
    });
    this.useEffect();
  }

  useEffect() {
    effect(
      () => {
        if (this.selectedDetail()) {
          try {
            this.form.patchValue(this.selectedDetail());
          } catch (e) {
            console.log('e', e);
          }
        }
      },
      { injector: this._injector }
    );
  }

  ngOnDestroy() {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  onDiscard() {
    this.onClose();
  }

  onSave() {
    try {
      if (!this.form.get('id').value) {
        this._action.actions.createDetail({ body: this.form.getRawValue() });
        this._action.actions.createDetailSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
          this.getDetailReportWithCallback(() => this.save.emit());
        });
        return;
      }

      this._action.actions.updateDetail({ id: this.form.get('id').value, body: this.form.getRawValue() });
      this._action.actions.updateDetailSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        this.getDetailReportWithCallback(() => this.save.emit());
      });
    } catch (e) {
      console.log('error', e);
    }
  }

  onClose() {
    this._matDialog.closeAll();
  }

  onBack() {
    this.back.emit();
  }

  onDelete() {
    this.popupService.openConfirmationDialog({
      title: this.translocoService.translate('ship_interface.delete_inventory'),
      message: this.translocoService.translate('ship_interface.are_you_sure_you_want_to_delete_this_inventory_this_action_cannot_be_undone'),
      actionType: 'delete',
      onAccept: () => {
        if (this.form.get('id').value) {
          this._action.actions.deleteDetail({ id: this.form.get('id').value });
          this._action.actions.deleteDetailSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
            this.getDetailReportWithCallback(() => this.back.emit());
          });
          return;
        }
      },
      onCancel: () => {}
    });
  }

  getDetailReportWithCallback(cb?: () => void): void {
    this._action.actions.getDetailReport({ report_id: this.detailReport().id });
    if (cb) {
      this._action.actions.getDetailReportSuccess$.pipe(take(1), takeUntil(this._unsubscribeAll)).subscribe(() => {
        cb();
      });
    }
  }

  onInventories() {
    this._robService
      .searchInventoryItemListByName('')
      .pipe(
        take(1),
        map((response: any) => response.data),
        tap((inventoryItems: InventoryItem[]) => {
          this.state.set({ inventories: inventoryItems });
        }),
        takeUntil(this._unsubscribeAll)
      )
      .subscribe();
    this.viewMode.set('select-inventory');
  }

  onSaveInventory(selectedInventoryId: any) {
    this.form.get('inventory_item_id').patchValue(selectedInventoryId);
    this.viewMode.set('add-edit');
  }
}
